import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

// Get safe return URL (only same-origin paths allowed)
function getSafeReturnUrl(returnUrl: string | null): string {
  if (!returnUrl) return '/';
  // Only allow paths starting with /
  if (returnUrl.startsWith('/')) return returnUrl;
  return '/';
}

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleOAuthCallback } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = searchParams.get('accessToken');
    const refreshToken = searchParams.get('refreshToken');
    const errorParam = searchParams.get('error');

    // Get return URL from localStorage (saved before OAuth redirect)
    const storedReturnUrl = localStorage.getItem('authReturnUrl');
    const returnUrl = getSafeReturnUrl(storedReturnUrl);

    if (errorParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional error handling on mount
      setError(errorParam);
      localStorage.removeItem('authReturnUrl');
      return;
    }

    if (accessToken && refreshToken) {
      handleOAuthCallback(accessToken, refreshToken)
        .then(() => {
          // Clear stored return URL and navigate
          localStorage.removeItem('authReturnUrl');
          navigate(returnUrl);
        })
        .catch((err) => {
          localStorage.removeItem('authReturnUrl');
          setError(err.message || '로그인에 실패했습니다');
        });
    } else {
      localStorage.removeItem('authReturnUrl');
      setError('인증 정보가 없습니다');
    }
  }, [searchParams, handleOAuthCallback, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="card text-center max-w-md">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-2xl"
            style={{ backgroundColor: 'rgba(225, 6, 0, 0.1)' }}
          >
            !
          </div>
          <h2 className="text-xl font-bold mb-2">로그인 실패</h2>
          <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-lg font-bold"
            style={{
              backgroundColor: 'var(--color-accent-red)',
              color: 'white',
            }}
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="card text-center">
        <div
          className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
          style={{ borderColor: 'var(--color-accent-red)', borderTopColor: 'transparent' }}
        />
        <p style={{ color: 'var(--color-text-secondary)' }}>로그인 처리 중...</p>
      </div>
    </div>
  );
}
